import React from 'react';
import { CheckCircle2, Circle, AlertTriangle } from 'lucide-react';

/**
 * Talent Lifecycle Timeline — Invited → Confirmed → Day-Confirmed →
 * Checked-in → Completed.
 *
 * Pure presentational. Pass `events` derived from a booking row:
 *   { invited_at, confirmed_at, day_confirmed_at, day_confirm_missed_at,
 *     clock_in_time, clock_out_time, status }
 */
const STAGES = [
  { key: 'invited',      label: 'Invited' },
  { key: 'confirmed',    label: 'Confirmed' },
  { key: 'day_confirmed', label: 'Day Confirmed' },
  { key: 'checked_in',   label: 'Checked In' },
  { key: 'completed',    label: 'Completed' },
];

const isReached = (key, e) => {
  switch (key) {
    case 'invited':       return Boolean(e.invited_at || e.confirmed_at || e.day_confirmed_at || e.clock_in_time || e.status === 'completed');
    case 'confirmed':     return Boolean(e.confirmed_at || e.day_confirmed_at || e.clock_in_time || e.status === 'completed');
    case 'day_confirmed': return Boolean(e.day_confirmed_at || e.clock_in_time || e.status === 'completed');
    case 'checked_in':    return Boolean(e.clock_in_time || e.clock_out_time || e.status === 'completed');
    case 'completed':     return Boolean(e.clock_out_time || e.status === 'completed');
    default:              return false;
  }
};

const isAtRisk = (key, e) => key === 'day_confirmed' && Boolean(e.day_confirm_missed_at) && !e.day_confirmed_at;

const LifecycleTimeline = ({ events = {}, dataTestId = 'lifecycle-timeline' }) => {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-3 overflow-hidden" data-testid={dataTestId}>
      <div className="text-xs font-semibold text-gray-700 mb-3">Talent Lifecycle</div>
      <ol className="flex items-center w-full">
        {STAGES.map((s, idx) => {
          const reached = isReached(s.key, events);
          const atRisk  = isAtRisk(s.key, events);
          const Icon = atRisk ? AlertTriangle : reached ? CheckCircle2 : Circle;
          const colorIcon = atRisk
            ? 'text-red-600'
            : reached ? 'text-emerald-600' : 'text-gray-300';
          const colorLabel = atRisk
            ? 'text-red-700 font-semibold'
            : reached ? 'text-gray-900 font-medium' : 'text-gray-400';
          const lineColor = reached ? 'bg-emerald-400' : 'bg-gray-200';
          return (
            <li
              key={s.key}
              className={`flex items-center ${idx < STAGES.length - 1 ? 'flex-1 min-w-0' : 'shrink-0'}`}
              data-testid={`lifecycle-stage-${s.key}`}
            >
              <div className="flex flex-col items-center w-12 sm:w-16 shrink-0">
                <Icon className={`w-5 h-5 ${colorIcon}`} />
                <span className={`text-[9px] sm:text-[10px] mt-1 text-center leading-tight break-words ${colorLabel}`}>
                  {s.label}
                </span>
              </div>
              {idx < STAGES.length - 1 && (
                <div className={`flex-1 h-0.5 mx-0.5 sm:mx-1 ${lineColor}`} />
              )}
            </li>
          );
        })}
      </ol>
      {events.day_confirm_missed_at && !events.day_confirmed_at && (
        <div className="mt-2 text-[11px] text-red-700" data-testid="lifecycle-at-risk-text">
          ⚠ Talent has not day-confirmed — flagged at-risk by automatic sweep.
        </div>
      )}
    </div>
  );
};

export default LifecycleTimeline;
